import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Star, Loader2, X, ExternalLink, Copy, Check } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { readFileAsDataUrl } from "@/lib/fileReader";
import { useReviewProof } from "@/hooks/useRewards";
import { REWARDS } from "@/constants/testIds";

const PLATFORMS = [
  { key: "google", label: "Google", points: 10 },
  { key: "instagram", label: "Instagram", points: 10 },
  { key: "facebook", label: "Facebook", points: 10 },
  // Iter-358 — TikTok stays on classifieds web; included in the composer.
  { key: "tiktok", label: "TikTok", points: 10 },
];

const MAX_BYTES = 6 * 1024 * 1024;

/**
 * Review-proof card. Customer picks a platform, we deep-link them to the
 * store's review page (Google gets a Places-style search URL), then they
 * upload a screenshot proving the review posted. Iter-449 — flat +10 pts
 * per platform proof; Google once per store; max 3 reward events per
 * store per day (backend-enforced).
 */
export default function ReviewProofCard({ storeId, store }) {
  const [platform, setPlatform] = useState("google");
  const [preview, setPreview] = useState("");
  const [uploadedUrl, setUploadedUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const mut = useReviewProof();

  const disabled = !storeId;
  const selected = PLATFORMS.find((p) => p.key === platform);

  // Best-effort platform deep-link. Google uses Places search on the store
  // name so we don't have to store per-store review URLs; Insta/FB fall
  // back to the store's handle if the backend provides one.
  const openReviewPage = () => {
    if (!store) return;
    let url = "";
    if (platform === "google") {
      const q = encodeURIComponent(`${store.name} review`);
      url = `https://www.google.com/maps/search/?api=1&query=${q}`;
    } else if (platform === "instagram") {
      const handle = (store.instagram || "").replace(/^@/, "");
      url = handle
        ? `https://www.instagram.com/${encodeURIComponent(handle)}/`
        : `https://www.instagram.com/explore/tags/${encodeURIComponent(
            (store.name || "").replace(/\s+/g, "")
          )}/`;
    } else if (platform === "facebook") {
      const handle = (store.facebook || "").replace(/^@/, "");
      url = handle
        ? `https://www.facebook.com/${encodeURIComponent(handle)}/reviews`
        : `https://www.facebook.com/search/top?q=${encodeURIComponent(store.name || "")}`;
    } else if (platform === "tiktok") {
      const handle = (store.tiktok || "").replace(/^@/, "");
      url = handle
        ? `https://www.tiktok.com/@${encodeURIComponent(handle)}`
        : `https://www.tiktok.com/search?q=${encodeURIComponent(store.name || "")}`;
    }
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("Screenshot must be under 6 MB.");
      return;
    }
    setUploading(true);
    try {
      const dataUri = await readFileAsDataUrl(file);
      setPreview(dataUri);
      const { data } = await sharedApi.post("/uploads/image", { data: dataUri });
      if (data?.url) setUploadedUrl(data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!storeId) {
      toast.error("Pick a store first.");
      return;
    }
    if (!uploadedUrl) {
      toast.error("Upload the screenshot first.");
      return;
    }
    try {
      await mut.mutateAsync({ store_id: storeId, platform, screenshot_url: uploadedUrl });
      toast.success(`Review proof submitted (+${selected?.points || 10} points once reviewed).`);
      setPreview("");
      setUploadedUrl("");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't submit review proof";
      toast.error(err?.response?.status === 409 ? "You've already earned this reward." : detail);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid={REWARDS.reviewCard}
      className="rounded-2xl bg-white shadow-card p-5 md:p-6"
    >
      <div className="flex items-center gap-2 text-sm font-display font-bold text-slate-900 mb-1">
        <Star className="w-4 h-4 text-[color:var(--brand-orange)]" /> Leave a review for points
      </div>
      <p className="text-xs text-slate-500 mb-3">
        +10 pts per platform · Google once per store · max 3 a day.
      </p>

      {/* Iter-456 — status chips for proofs already submitted, so the
          customer knows their points are on the way. */}
      <MyProofChips />

      <div
        data-testid={REWARDS.reviewPlatform}
        className="flex flex-wrap gap-2 mb-3"
        role="radiogroup"
        aria-label="Review platform"
      >
        {PLATFORMS.map((p) => (
          <button
            key={p.key}
            type="button"
            role="radio"
            aria-checked={platform === p.key}
            onClick={() => setPlatform(p.key)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${
              platform === p.key
                ? "bg-[color:var(--brand-blue)] text-white border-transparent"
                : "bg-white text-slate-700 border-soft hover:border-[color:var(--brand-blue)]"
            }`}
          >
            {p.label} +{p.points}
          </button>
        ))}
      </div>

      <button
        type="button"
        data-testid={REWARDS.reviewOpenGoogle}
        onClick={openReviewPage}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[color:var(--brand-blue)] hover:underline mb-3 disabled:opacity-50"
      >
        Open {selected?.label || "review"} page <ExternalLink className="w-3 h-3" />
      </button>

      <ComposerBlock storeId={storeId} platform={platform} platformLabel={selected?.label || platform} disabled={disabled} />

      <div className="flex items-center gap-3">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
          Screenshot
        </span>
        {preview ? (
          <div className="relative w-16 h-16 rounded-xl overflow-hidden border border-soft">
            <img src={preview} alt="Review proof" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => { setPreview(""); setUploadedUrl(""); }}
              className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-white/90 grid place-items-center text-slate-600 hover:text-red-500"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[color:var(--brand-blue)] text-[color:var(--brand-blue)] px-3 py-1.5 text-xs font-semibold cursor-pointer">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "+ Upload screenshot"}
            <input
              data-testid={REWARDS.reviewScreenshotInput}
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={(e) => onFile(e.target.files?.[0])}
              disabled={disabled || uploading}
            />
          </label>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="submit"
          data-testid={REWARDS.reviewSubmit}
          disabled={mut.isPending || disabled}
          className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-5 py-2.5 disabled:opacity-60 transition-colors"
        >
          {mut.isPending && <Loader2 className="w-4 h-4 animate-spin" />} Submit for review
        </button>
      </div>
    </form>
  );
}

/**
 * Iter-358 — review/post composer (mirror of the app screen). Calls the
 * live `POST /api/rewards/compose-post` for a ready-to-paste draft; each
 * tap regenerates a fresh one. Founder-approved button copy verbatim.
 */
function ComposerBlock({ storeId, platform, platformLabel, disabled }) {
  const [copied, setCopied] = useState(false);
  const mut = useMutation({
    mutationFn: async () =>
      (await sharedApi.post("/rewards/compose-post", { store_id: storeId, platform })).data,
    onError: (err) =>
      toast.error(err?.response?.data?.detail || "Couldn't write a draft — try again."),
  });
  // A draft is platform+store specific: hide stale text after a switch.
  const draft =
    mut.data && mut.data.platform === platform ? mut.data.text : "";

  const buttonCopy =
    platform === "google" ? "✨ Write my review for me" : "✨ Write my post for me";

  const copyDraft = async () => {
    try {
      await navigator.clipboard.writeText(draft);
    } catch {
      // Clipboard API unavailable (e.g. non-secure context) — legacy path.
      const ta = document.createElement("textarea");
      ta.value = draft;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mb-3">
      <button
        type="button"
        data-testid="rewards-compose-btn"
        onClick={() => mut.mutate()}
        disabled={disabled || mut.isPending}
        className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--brand-blue)] text-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-wash)] px-3.5 py-1.5 text-xs font-bold transition-colors disabled:opacity-50"
      >
        {mut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        {buttonCopy}
      </button>

      {draft && (
        <div className="mt-2">
          <textarea
            data-testid="rewards-compose-text"
            readOnly
            value={draft}
            rows={Math.min(6, Math.max(3, Math.ceil(draft.length / 60)))}
            onFocus={(e) => e.target.select()}
            className="w-full rounded-xl border border-soft bg-slate-50 px-3 py-2.5 text-sm text-slate-800 leading-relaxed focus:outline-none focus:border-[color:var(--brand-blue)]"
          />
          <div className="mt-1 flex items-center justify-between gap-3">
            <p className="text-[11px] text-slate-500" data-testid="rewards-compose-hint">
              Copy this, publish it on {platformLabel}, then upload a screenshot below 👇
              <span className="block" data-testid="review-bonus-hint">
                Bonus points need at least 20 words or a photo — up to 3 bonuses a day, 10 a month.
              </span>
            </p>
            <button
              type="button"
              data-testid="rewards-compose-copy"
              onClick={copyDraft}
              className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-[color:var(--brand-blue)] hover:underline"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3" /> Copied!
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" /> Copy
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Iter-456 — "my proofs" status chips (GET /rewards/my-proofs).
 * Pending chips reassure the customer their points are coming;
 * approved/rejected show the outcome. Hidden when there are none.
 */
function MyProofChips() {
  const q = useQuery({
    queryKey: ["my-proofs"],
    retry: 0,
    staleTime: 30_000,
    queryFn: async () => (await sharedApi.get("/rewards/my-proofs")).data,
  });
  const proofs = q.data?.proofs || [];
  if (!proofs.length) return null;
  const cls = {
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-red-50 text-red-600 border-red-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <div className="mb-3" data-testid="my-proof-chips">
      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5">
        Your submitted proofs
      </div>
      <div className="flex flex-wrap gap-1.5">
        {proofs.slice(0, 8).map((p, i) => {
          const status = String(p.status || "pending").toLowerCase();
          const label = [p.platform, p.store_name || p.vendor_name].filter(Boolean).join(" · ") || "Proof";
          return (
            <span
              key={p.id || i}
              data-testid={`my-proof-chip-${p.id || i}`}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold capitalize ${cls[status] || cls.pending}`}
            >
              {status === "approved" ? <Check className="w-3 h-3" /> : status === "rejected" ? <X className="w-3 h-3" /> : <Loader2 className="w-3 h-3" />}
              {label} — {status === "pending" ? "pending approval" : status}
            </span>
          );
        })}
      </div>
    </div>
  );
}
